import React, { FC } from 'react';
import { connect } from 'react-redux';
import { Form, Input, Modal, Radio } from 'antd';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import { withUserType } from '../withUserType';
import { ComplianceType } from '../../types/ComplianceTypes';
import { nullSafeGet } from '../../utils/DataAccessUtils';

interface ComplianceTypeFormModalProps {
	visible: boolean;
	// Which compliance endpoints to hit. Falls back to the logged-in user's role
	// (session.userType, injected by withUserType) when not explicitly provided.
	roleType?: string;
	editingComplianceType?: ComplianceType;
	// Prefills the name on a fresh create (e.g. the "+ Create new type" flow in
	// the request wizard). Ignored when editingComplianceType is set.
	initialName?: string;
	onCancel: () => void;
	onSuccess: (complianceType: ComplianceType) => void;
	saving: boolean;
	createComplianceType: (entity: any) => Promise<any>;
	updateComplianceType: (entity: any) => Promise<any>;
}

const ComplianceTypeFormModal: FC<ComplianceTypeFormModalProps> = ({
	visible,
	editingComplianceType,
	initialName,
	onCancel,
	onSuccess,
	saving,
	createComplianceType,
	updateComplianceType,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const isEditing = !!editingComplianceType;

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			const entity = { ...values, countryCode: 'US' };
			const save = isEditing
				? updateComplianceType({ ...editingComplianceType, ...entity })
				: createComplianceType(entity);
			save.then((savedComplianceType) => {
				form.resetFields();
				onSuccess(savedComplianceType);
			});
		});
	};

	const handleCancel = () => {
		form.resetFields();
		onCancel();
	};

	return (
		<Modal
			visible={visible}
			title={isEditing ? 'Edit Compliance Type' : 'New Compliance Type'}
			okText={isEditing ? 'SAVE' : 'CREATE COMPLIANCE TYPE'}
			cancelText="CANCEL"
			onOk={handleSubmit}
			onCancel={handleCancel}
			confirmLoading={saving}
			destroyOnClose={true}
			width={587}
		>
			<Form
				form={form}
				layout="vertical"
				preserve={false}
				initialValues={{
					name: nullSafeGet('name', editingComplianceType) || initialName,
					allowSampleUpload: nullSafeGet('allowSampleUpload', editingComplianceType) || false,
					requiresUniqueDocument:
						nullSafeGet('requiresUniqueDocument', editingComplianceType) || false,
					isLocationSpecific: nullSafeGet('isLocationSpecific', editingComplianceType) || false,
				}}
			>
				<Form.Item
					name="name"
					label="Compliance Type"
					rules={[{ required: true, message: 'Please name this compliance type.' }]}
				>
					<Input placeholder="Certificate of Insurance" />
				</Form.Item>
				<Form.Item
					name="allowSampleUpload"
					label="Show option to upload sample document?"
					rules={[{ required: true, message: 'Please choose an option.' }]}
				>
					<Radio.Group>
						<div className="flex flex-col gap-2">
							<Radio value={true}>Yes</Radio>
							<Radio value={false}>No</Radio>
						</div>
					</Radio.Group>
				</Form.Item>
				<Form.Item
					name="requiresUniqueDocument"
					label="Does this compliance type require a unique document from a supplier?"
					rules={[{ required: true, message: 'Please choose an option.' }]}
				>
					<Radio.Group>
						<div className="flex flex-col gap-2">
							<Radio value={true}>Yes, varies by request.</Radio>
							<Radio value={false}>No, accept general documents</Radio>
						</div>
					</Radio.Group>
				</Form.Item>
				<Form.Item
					name="isLocationSpecific"
					label="Is this compliance type location specific?"
					rules={[{ required: true, message: 'Please choose an option.' }]}
				>
					<Radio.Group>
						<div className="flex flex-col gap-2">
							<Radio value={true}>Yes, varies by request</Radio>
							<Radio value={false}>No, applies to all locations</Radio>
						</div>
					</Radio.Group>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	saving: state.compliance_types.creating || state.compliance_types.updating,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		createComplianceType: (entity) => dispatch(complianceTypesRestCrudThunks(role).create(entity)),
		updateComplianceType: (entity) => dispatch(complianceTypesRestCrudThunks(role).update(entity)),
	};
};

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(ComplianceTypeFormModal));
